import React, { useEffect, useState } from 'react';
import './App.css';
import emailjs from '@emailjs/browser';
import { addDoc, collection, limit, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const groomingComparisons = Array.from({ length: 8 }, (_, index) => ({
  id: index + 1,
  before: `/comparacion/${index + 1}_antes.jpeg`,
  after: `/comparacion/${index + 1}_despues.jpeg`,
}));

const formatReviewDate = (timestamp) => {
  if (!timestamp?.toDate) return 'Recién publicada';

  return new Intl.DateTimeFormat('es-PR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(timestamp.toDate());
};

function ReviewsFamilyPage() {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const submitReview = async (event) => {
    event.preventDefault();
    if (!rating) {
      setError('Selecciona una calificación con estrellas.');
      return;
    }

    if (website) {
      setSubmitted(true);
      return;
    }

    setSending(true);
    setError('');

    try {
      await addDoc(collection(db, 'reviews'), {
        authorName: name.trim(),
        rating,
        comment: comment.trim(),
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (submitError) {
      console.error('Error publishing public review', submitError);
      setError('No se pudo publicar la reseña. Intenta nuevamente más tarde.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="public-review-page">
      <section className="public-review-card">
        <a className="public-review-brand" href="/">Family Pet Hotel</a>
        {submitted ? (
          <div className="public-review-success">
            <h1>¡Gracias por tu reseña!</h1>
            <p>Tu experiencia ya fue compartida con Family Pet Hotel.</p>
            <a className="btn btn-primary" href="/">Volver al sitio</a>
          </div>
        ) : (
          <>
            <h1>Comparte tu experiencia</h1>
            <p className="public-review-intro">Tu opinión ayuda a otras familias a conocer nuestro servicio.</p>
            {error && <p className="public-review-error" role="alert">{error}</p>}
            <form onSubmit={submitReview}>
              <div className="form-group">
                <label htmlFor="public-review-name">Tu nombre</label>
                <input
                  id="public-review-name"
                  type="text"
                  autoComplete="name"
                  maxLength="60"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="form-group">
                <span className="public-review-label">Tu calificación</span>
                <div className="rating-stars" role="radiogroup" aria-label="Tu calificación">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`rating-star ${rating >= star ? 'selected' : ''}`}
                      role="radio"
                      aria-checked={rating === star}
                      aria-label={`${star} ${star === 1 ? 'estrella' : 'estrellas'}`}
                      onClick={() => setRating(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="public-review-comment">Tu reseña</label>
                <textarea
                  id="public-review-comment"
                  rows="6"
                  minLength="10"
                  maxLength="600"
                  required
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                ></textarea>
              </div>
              <div className="review-honeypot" aria-hidden="true">
                <label htmlFor="review-website">Sitio web</label>
                <input
                  id="review-website"
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={sending}>
                {sending ? 'Enviando...' : 'Enviar reseña'}
              </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}

function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const reviewsQuery = query(
      collection(db, 'reviews'),
      orderBy('createdAt', 'desc'),
      limit(6)
    );

    const unsubscribe = onSnapshot(
      reviewsQuery,
      (snapshot) => {
        setReviews(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setReviewsLoadError(false);
      },
      (error) => {
        console.error('Error loading reviews', error);
        setReviewsLoadError(true);
      }
    );

    return unsubscribe;
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  // form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoadError, setReviewsLoadError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      // Save to Firestore
      await addDoc(collection(db, 'contact_messages'), {
        name,
        email,
        phone,
        service,
        message,
        createdAt: serverTimestamp(),
      });

      // Initialize EmailJS con tu Public Key
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'TU_PUBLIC_KEY_DE_EMAILJS');

      // Email 1: Enviar a jr218516@gmail.com (Administrador)
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'TU_SERVICE_ID',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ADMIN || 'TU_TEMPLATE_ID_ADMIN',
        {
          to_email: 'jr218516@gmail.com',
          from_name: name,
          from_email: email,
          phone: phone,
          service: service,
          message: message,
          reply_to: email,
        }
      );

      // Email 2: Enviar confirmación al cliente
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'TU_SERVICE_ID',
        process.env.REACT_APP_EMAILJS_TEMPLATE_CLIENT || 'TU_TEMPLATE_ID_CLIENTE',
        {
          to_email: email,
          to_name: name,
          service: service,
          reply_to: 'jr218516@gmail.com',
        }
      );

      alert('¡Mensaje enviado exitosamente! Recibirás una confirmación por email.');

      // reset form
      setName('');
      setEmail('');
      setPhone('');
      setService('');
      setMessage('');
    } catch (err) {
      console.error('Error sending message', err);
      alert('Ocurrió un error al enviar tu mensaje. Intenta de nuevo o llámanos directamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/logo.png" alt="Family Pet Hotel" className="logo-img" />
            <span className="logo-text">FAMILY PET HOTEL</span>
          </div>
          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <a href="#inicio" className="nav-link" onClick={() => scrollToSection('inicio')}>Inicio</a>
            <a href="#servicios" className="nav-link" onClick={() => scrollToSection('servicios')}>Servicios</a>
            <a href="#instalaciones" className="nav-link" onClick={() => scrollToSection('instalaciones')}>Instalaciones</a>
            <a href="#galeria" className="nav-link" onClick={() => scrollToSection('galeria')}>Galería</a>
            <a href="#resenas" className="nav-link" onClick={() => scrollToSection('resenas')}>Reseñas</a>
            <a href="#sobre-nosotros" className="nav-link" onClick={() => scrollToSection('sobre-nosotros')}>Sobre Nosotros</a>
            <a href="#contacto" className="nav-link" onClick={() => scrollToSection('contacto')}>Contacto</a>
          </div>
          <button
            className={`hamburger ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMenuOpen}
            type="button"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>


      {/* Hero Section */}
      <section id="inicio" className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-eyebrow">Family Pet Hotel · Aibonito, Puerto Rico</div>
            <h1 className="hero-title">Pet hotel, grooming y cuido para mascotas en Aibonito</h1>
            <p className="hero-subtitle">
              Cuidamos a tu perro como en casa, en un espacio limpio, familiar y supervisado.
            </p>
            <div className="hero-proof-grid" aria-label="Servicios destacados">
              <div>
                <strong>24/7</strong>
                <span>Supervisión</span>
              </div>
              <div>
                <strong>3</strong>
                <span>Servicios</span>
              </div>
              <div>
                <strong>Aibonito</strong>
                <span>Puerto Rico</span>
              </div>
            </div>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={() => scrollToSection('contacto')}>
                Reservar Ahora
              </button>
              <button className="btn btn-secondary" onClick={() => scrollToSection('servicios')}>
                Ver Servicios
              </button>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-photo-composition" aria-label="Fotos de Family Pet Hotel">
              <div className="hero-main-photo">
                <img src="/instalaciones/4.jpeg" alt="Perro listo después de grooming en Family Pet Hotel" />
              </div>
              <div className="hero-mini-photo hero-mini-photo-left">
                <img src="/intro.jpeg" alt="Logo iluminado de Family Pet Hotel" />
              </div>
              <div className="hero-mini-photo hero-mini-photo-right">
                <img src="/instalaciones/8.jpeg" alt="Área de grooming e instalaciones" />
              </div>
              <div className="hero-trust-card">
                <span>Supervisión cercana</span>
                <strong>Ambiente familiar</strong>
              </div>
              <div className="hero-service-tags">
                <span>Hotel</span>
                <span>Grooming</span>
                <span>Cuido diurno</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="services">
        <div className="container">
          <h2 className="section-title">Nuestros Servicios</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">🏨</div>
              <h3>Hotel para Perros</h3>
              <p>Hospedaje cómodo y seguro para tu mascota con atención 24/7</p>
              <ul>
                <li>Habitaciones climatizadas</li>
                <li>Ejercicio diario</li>
                <li>Alimentación personalizada</li>
                <li>Monitoreo constante</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">✂️</div>
              <h3>Grooming Profesional</h3>
              <p>Servicios completos de belleza y cuidado para tu perro</p>
              <ul>
                <li>Baño y secado profesional</li>
                <li>Corte de pelo estilizado</li>
                <li>Limpieza de oídos</li>
                <li>Corte de uñas</li>
                <li>Cepillado dental</li>
              </ul>
            </div>
            <div className="service-card">
              <div className="service-icon">🎾</div>
              <h3>Cuido Diurno</h3>
              <p>Cuidado diurno con actividades y socialización</p>
              <ul>
                <li>Juegos supervisados</li>
                <li>Horarios flexibles</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Services Section Photo Frames */}
        <div className="section-photo-frames">
          <div className="photo-frame services-frame-1">
            <div className="frame-border">
              <div className="photo-content">🐾</div>
            </div>
          </div>
          <div className="photo-frame services-frame-2">
            <div className="frame-border">
              <div className="photo-content">🐕‍🦺</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre-nosotros" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">Sobre Nosotros</h2>
              <p className="about-description">
                Bienvenidos al <strong>Family Pet Hotel</strong>, donde tu mascota es parte de nuestra familia. 
                Ubicados en el hermoso pueblo de Aibonito, Puerto Rico, ofrecemos un ambiente seguro, cómodo y lleno 
                de amor para tu mejor amigo.
              </p>
              <p className="about-description">
                Bajo la dirección de <strong>Jahaira Rodriguez</strong>, nuestro equipo de profesionales está dedicado a brindar 
                el mejor cuidado para tu mascota, ya sea durante una estadía en nuestro hotel o durante un 
                servicio de grooming profesional.
              </p>
              <p className="about-description">
                Recibimos familias de <strong>Aibonito, Cayey, Cidra, Barranquitas y Salinas</strong> que buscan pet hotel,
                grooming y cuido confiable para sus mascotas. Nuestros servicios se enfocan principalmente en perros;
                si necesitas orientación para gatos, contáctanos para confirmar disponibilidad.
              </p>
              <div className="about-features">
                <div className="feature">
                  <span className="feature-icon">❤️</span>
                  <span>Amor y cuidado personalizado</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🏆</span>
                  <span>Profesionales experimentados</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🏠</span>
                  <span>Ambiente familiar y acogedor</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📱</span>
                  <span>Actualizaciones constantes</span>
                </div>
              </div>
            </div>
            <div className="about-image">
              <img src="/intro.jpeg" alt="Instalaciones Family Pet Hotel" className="facilities-img" />
            </div>
          </div>
        </div>
        
        {/* About Section Photo Frames */}
        <div className="section-photo-frames">
          <div className="photo-frame about-frame-1">
            <div className="frame-border">
              <div className="photo-content">🐶</div>
            </div>
          </div>
          <div className="photo-frame about-frame-2">
            <div className="frame-border">
              <div className="photo-content">🐕‍🦺</div>
            </div>
          </div>
          <div className="photo-frame about-frame-3">
            <div className="frame-border">
              <div className="photo-content">🐾</div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section id="instalaciones" className="facilities">
        <div className="container">
          <h2 className="section-title">Nuestras Instalaciones</h2>
          <div className="facilities-grid">
            <div className="facility-card">
              <div className="facility-image">🛏️</div>
              <h3>Habitaciones Cómodas</h3>
              <p>Espacios amplios y climatizados para el descanso perfecto</p>
            </div>
            <div className="facility-card">
              <div className="facility-image">🚿</div>
              <h3>Sala de Grooming</h3>
              <p>Equipamiento profesional para servicios de belleza de primera</p>
            </div>
            <div className="facility-card">
              <div className="facility-image">🌳</div>
              <h3>Patio para pasear</h3>
              <p>Espacios al aire libre seguros para pasear a su mascota</p>
            </div>
            <div className="facility-card">
              <div className="facility-image">📹</div>
              <h3>Monitoreo 24/7</h3>
              <p>Supervisión constante para la seguridad de tu mascota</p>
            </div>
          </div>
        </div>
        
        {/* Facilities Section Photo Frames */}
        <div className="section-photo-frames">
          <div className="photo-frame facilities-frame-1">
            <div className="frame-border">
              <div className="photo-content">🐩</div>
            </div>
          </div>
          <div className="photo-frame facilities-frame-2">
            <div className="frame-border">
              <div className="photo-content">🦮</div>
            </div>
          </div>
          <div className="photo-frame facilities-frame-3">
            <div className="frame-border">
              <div className="photo-content">🐕</div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galeria" className="gallery-section">
        <div className="container">
          <h2 className="section-title">Galería</h2>
          <div className="photo-gallery">
            <div className="photo-item">
              <img src="/instalaciones/1.jpeg" alt="Instalaciones Family Pet Hotel" />
            </div>
            <div className="photo-item">
              <img src="/instalaciones/4.jpeg" alt="Instalaciones Family Pet Hotel" />
            </div>
            <div className="photo-item">
              <img src="/instalaciones/3.jpeg" alt="Instalaciones Family Pet Hotel" />
            </div>
            <div className="photo-item">
              <img src="/instalaciones/5.jpeg" alt="Instalaciones Family Pet Hotel" />
            </div>
            <div className="photo-item">
              <img src="/instalaciones/6.jpeg" alt="Instalaciones Family Pet Hotel" />
            </div>
            <div className="photo-item">
              <img src="/instalaciones/8.jpeg" alt="Instalaciones Family Pet Hotel" />
            </div>
          </div>
        </div>
      </section>

      {/* Before and After Section */}
      <section id="antes-despues" className="before-after-section">
        <div className="container">
          <div className="before-after-heading">
            <p className="section-eyebrow">Grooming con cariño</p>
            <h2 className="section-title">Antes y después</h2>
            <p>Conoce algunas de las transformaciones realizadas por nuestro equipo.</p>
          </div>

          <div className="before-after-grid">
            {groomingComparisons.map((comparison) => (
              <figure className="comparison-card" key={comparison.id}>
                <div className="comparison-photos">
                  <div className="comparison-photo">
                    <span className="comparison-label comparison-label-before">Antes</span>
                    <img
                      src={comparison.before}
                      alt={`Mascota antes del grooming, transformación ${comparison.id}`}
                      loading="lazy"
                    />
                  </div>
                  <div className="comparison-photo">
                    <span className="comparison-label comparison-label-after">Después</span>
                    <img
                      src={comparison.after}
                      alt={`Mascota después del grooming, transformación ${comparison.id}`}
                      loading="lazy"
                    />
                  </div>
                </div>
                <figcaption>
                  <span aria-hidden="true">✦</span>
                  Transformación {String(comparison.id).padStart(2, '0')}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="resenas" className="reviews-section">
        <div className="container">
          <div className="reviews-heading">
            <p className="section-eyebrow">Experiencias reales</p>
            <h2 className="section-title">Reseñas de nuestros clientes</h2>
            <p>Lo que comparten las familias que han confiado en nosotros.</p>
          </div>

          <div className="reviews-list" aria-live="polite">
            {reviewsLoadError ? (
              <p className="reviews-empty">No pudimos cargar las reseñas en este momento.</p>
            ) : reviews.length === 0 ? (
              <p className="reviews-empty">Aún no hay reseñas publicadas.</p>
            ) : (
              reviews.map((review) => (
                <article className="review-card" key={review.id}>
                  <div className="review-card-header">
                    <div>
                      <h3>{review.authorName}</h3>
                      <p>{formatReviewDate(review.createdAt)}</p>
                    </div>
                    <span className="review-stars" aria-label={`${review.rating} de 5 estrellas`}>
                      {'★'.repeat(review.rating || 0)}{'☆'.repeat(5 - (review.rating || 0))}
                    </span>
                  </div>
                  <p className="review-comment">{review.comment}</p>
                </article>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="contact">
        <div className="container">
          <h2 className="section-title">Contáctanos</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Información de Contacto</h3>
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <div>
                  <strong>Ubicación</strong>
                  <p>Aibonito, Puerto Rico<br />Ciudad de las Flores</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">👩‍💼</span>
                <div>
                  <strong>Propietaria</strong>
                  <p>Jahaira Rodriguez</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <div>
                  <strong>Teléfono</strong>
                  <p>(787) 479-4812</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <div>
                  <strong>Email</strong>
                  <p>jary2480157@yahoo.com</p>
                </div>
              </div>
              <div className="contact-item">
                <span className="contact-icon">🕐</span>
                <div>
                  <strong>Horarios</strong>
                  <p>Lun - Vie: 7:00 AM - 7:00 PM<br />
                     Sáb - Dom: 8:00 AM - 6:00 PM</p>
                </div>
              </div>
            </div>
            <div className="contact-form">
              <h3>Envíanos un Mensaje</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Tu nombre"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Tu email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    placeholder="Tu teléfono"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <select required value={service} onChange={(e) => setService(e.target.value)}>
                    <option value="">Selecciona un servicio</option>
                    <option value="hotel">Hotel para Perros</option>
                    <option value="grooming">Grooming</option>
                    <option value="guarderia">Cuido Diurno</option>
                    <option value="consulta">Consulta General</option>
                  </select>
                </div>
                <div className="form-group">
                  <textarea
                    placeholder="Tu mensaje"
                    rows="5"
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  ></textarea>
                </div>
                <button type="submit" className="btn btn-primary" disabled={sending}>
                  {sending ? 'Enviando...' : 'Enviar Mensaje'}
                </button>
              </form>
            </div>
          </div>
        </div>
        
        {/* Contact Section Photo Frames */}
        <div className="section-photo-frames">
          <div className="photo-frame contact-frame-1 polaroid-frame">
            <div className="frame-border">
              <div className="photo-content">🐕</div>
              <div className="photo-caption">Max</div>
            </div>
          </div>
          <div className="photo-frame contact-frame-2 polaroid-frame">
            <div className="frame-border">
              <div className="photo-content">🐩</div>
              <div className="photo-caption">Luna</div>
            </div>
          </div>
          <div className="photo-frame contact-frame-3 polaroid-frame">
            <div className="frame-border">
              <div className="photo-content">🦮</div>
              <div className="photo-caption">Bruno</div>
            </div>
          </div>
          <div className="photo-frame contact-frame-4 polaroid-frame">
            <div className="frame-border">
              <div className="photo-content">🐕‍🦺</div>
              <div className="photo-caption">Bella</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/logo.png" alt="Family Pet Hotel" className="footer-logo-img" />
              <h3>Family Pet Hotel</h3>
              <p>Tu mascota, nuestra familia</p>
              <div className="social-links">
                <a
                  href="https://www.facebook.com/p/Family-Pet-Hotel-and-Grooming-100095462313273/?locale=ru_RU"
                  aria-label="Facebook de Family Pet Hotel"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22 12.06C22 6.48 17.52 2 11.94 2S2 6.48 2 12.06c0 5.02 3.66 9.19 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.23.19 2.23.19v2.45h-1.26c-1.24 0-1.62.77-1.62 1.56v1.9h2.76l-.44 2.91h-2.32V22C18.34 21.25 22 17.08 22 12.06z" />
                  </svg>
                </a>
                <a
                  href="https://wa.me/17874794812"
                  aria-label="WhatsApp de Family Pet Hotel"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12.04 2C6.57 2 2.11 6.45 2.11 11.91c0 1.75.46 3.45 1.34 4.95L2 22l5.27-1.38a9.93 9.93 0 0 0 4.77 1.21h.01c5.47 0 9.92-4.45 9.92-9.91A9.86 9.86 0 0 0 19.06 4.9 9.84 9.84 0 0 0 12.04 2zm.01 18.15h-.01a8.25 8.25 0 0 1-4.21-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.17 8.17 0 0 1-1.25-4.37c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.82 2.41a8.18 8.18 0 0 1 2.42 5.82c0 4.54-3.69 8.23-8.23 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.97-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.21-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07s.89 2.4 1.02 2.57c.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="footer-links">
              <h4>Enlaces Rápidos</h4>
              <ul>
                <li><a href="#inicio" onClick={() => scrollToSection('inicio')}>Inicio</a></li>
                <li><a href="#servicios" onClick={() => scrollToSection('servicios')}>Servicios</a></li>
                <li><a href="#instalaciones" onClick={() => scrollToSection('instalaciones')}>Instalaciones</a></li>
                <li><a href="#galeria" onClick={() => scrollToSection('galeria')}>Galería</a></li>
                <li><a href="#antes-despues" onClick={() => scrollToSection('antes-despues')}>Antes y después</a></li>
                <li><a href="#resenas" onClick={() => scrollToSection('resenas')}>Reseñas</a></li>
                <li><a href="#contacto" onClick={() => scrollToSection('contacto')}>Contacto</a></li>
              </ul>
            </div>
            <div className="footer-contact">
              <h4>Contáctanos</h4>
              <p>📍 Aibonito, Puerto Rico</p>
              <p>📞 (787) 479-4812</p>
              <p>📧 jary2480157@yahoo.com</p>
              <div className="footer-hours">
                <strong>Horarios de Atención:</strong>
                <p>Lun - Vie: 7:00 AM - 7:00 PM</p>
                <p>Sáb - Dom: 8:00 AM - 6:00 PM</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Family Pet Hotel - Todos los derechos reservados | Diseñado con ❤️ para tu mascota</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return window.location.pathname === '/reviewsfamily' ? <ReviewsFamilyPage /> : <HomePage />;
}

export default App;
